import { Alert, AlertDescription, AlertIcon, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useActiveAllocationRuleUpdate } from "gen/endpoints/active-allocation-rule/active-allocation-rule";
import {
  useAllocationRuleListSuspense,
  useAllocationRuleRetrieveSuspense,
} from "gen/endpoints/allocation-rule/allocation-rule";
import { AllocationRuleEditActiveRuleDataRequest } from "gen/model/allocationRuleEditActiveRuleDataRequest";
import { AllocationRuleReadData } from "gen/model/allocationRuleReadData";
import { activeAllocationRuleUpdateBody } from "gen/schemas/active-allocation-rule/active-allocation-rule";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { EntitySelect } from "shared/components/input/select/EntitySelect";
import { TransactionCalculationSelect } from "shared/components/input/select/TransactionCalculationSelect";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { WarningIcon } from "shared/icons/WarningIcon";
import { useRoutePathTemplate } from "shared/navigation/UseRoutePathTemplate";
import { formatIsoDateToDisplay, nowAsIsoDate } from "shared/utils/DateUtils";
import { isTruthy } from "shared/utils/utils";

interface AllocationActiveEditDrawerProps {
  fundFamilyId: string;
  allocationRuleId: string;
  onCloseComplete: () => void;
}

export const AllocationActiveEditDrawer: React.FC<
  AllocationActiveEditDrawerProps
> = ({ fundFamilyId, allocationRuleId, onCloseComplete }) => {
  const { t } = useTranslation(["allocation", "common"]);
  const navigate = useNavigate();
  const params = useParams();
  const pathTemplate = useRoutePathTemplate();

  const { data: allocationRule } = useAllocationRuleRetrieveSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const { data: entityAllocationData } = useAllocationRuleListSuspense(
    fundFamilyId,
    {
      entity_id: allocationRule.entity_id,
    },
  );

  // Multiple allocation rules for an entity can't point to the same transaction calculation
  const existingTransactionCalculationIds = entityAllocationData
    .map((allocation) => allocation.transaction_calculation_id)
    .filter((id) => id !== allocationRule.transaction_calculation_id)
    .filter(isTruthy);

  const { mutateAsync: updateAllocationRule } = useActiveAllocationRuleUpdate();

  const form = useForm<AllocationRuleEditActiveRuleDataRequest>({
    resolver: zodResolver(activeAllocationRuleUpdateBody),
    defaultValues: {
      allocation_rule_name: allocationRule.name,
      transaction_calculation_id: allocationRule.transaction_calculation_id!,
      date: nowAsIsoDate(),
    },
  });

  const calculationId = useWatch({
    control: form.control,
    name: "transaction_calculation_id",
  });

  const date = useWatch({
    control: form.control,
    name: "date",
  });

  const isCalculationIdChanged =
    calculationId !== allocationRule.transaction_calculation_id;

  const onSubmit = async (data: AllocationRuleEditActiveRuleDataRequest) =>
    await updateAllocationRule({
      fundFamilyId: allocationRule.fund_family_id,
      allocationRuleId: allocationRule.id,
      data,
    });

  const onSuccess = (data: AllocationRuleReadData) => {
    navigate(
      generatePath(pathTemplate, { ...params, allocationRuleId: data.id }),
    );
  };

  return (
    <FormDrawer
      title={t("allocation:edit.title")}
      formProps={{
        form,
        onSubmit,
        onSuccess,
        successMessage: t("allocation:edited"),
      }}
      onCloseComplete={onCloseComplete}
    >
      {isCalculationIdChanged && (
        <Alert status="warning" mb="16px">
          <AlertIcon alignSelf="flex-start" mt="4px">
            <WarningIcon />
          </AlertIcon>
          <AlertDescription>
            {t("allocation:edit.calculationChangeWarning", {
              date: formatIsoDateToDisplay(date),
            })}
          </AlertDescription>
        </Alert>
      )}
      <FormTextInput name="allocation_rule_name" title={t("common:name")} />
      <TransactionCalculationSelect
        fundFamilyId={fundFamilyId}
        name="transaction_calculation_id"
        calculationIdsToIgnore={existingTransactionCalculationIds}
      />
      <EntitySelect
        name="entity_id"
        title={t("allocation:details.connectedEntity")}
        fundFamilyId={fundFamilyId}
        value={allocationRule.entity_id}
        isDisabled={true}
      />
      <Text pt="8px" pb="16px" textStyle="overline">
        {t("allocation:edit.ruleChangeDetails")}
      </Text>
      <FormDateInput
        name="date"
        title={t("allocation:edit.effectiveDateOfChange")}
      />
      <FormTextArea
        name="change_reason_notes"
        title={t("allocation:edit.reasonForChange")}
      />
    </FormDrawer>
  );
};
